// SPDX-License-Identifier: Apache-2.0

// nodes.ts — DUAL-INSTANCED-MESH doc nodes with distance-LOD glass panels.
//
// Two instanced meshes share the same per-instance positions and colours:
//   1. panelMesh — `RoundedBoxGeometry` + `MeshPhysicalMaterial` with
//      transmission/IOR/attenuation. Renders for instances closer than the
//      LOD switch radius. Tinted by the doc's `tint` via per-instance colour
//      and `attenuationColor` (the colour applies in the shader-side BSDF).
//   2. sphereMesh — `SphereGeometry` + `MeshStandardMaterial` (emissive).
//      Renders for instances farther than the LOD switch radius. Cheap
//      primitive that scales to 1000s of nodes under bloom.
//
// LOD HYSTERESIS:
//   - within 8.0 world units of the camera → glass panel (sphere hidden via
//     scale = 0)
//   - beyond 8.5 world units → sphere (panel hidden via scale = 0)
//   - in the 8.0-8.5 band, the previous frame's choice sticks. This kills
//     pop-flicker on near-tangent orbits.
//
// PERFORMANCE BUDGET:
//   - per-frame work is one O(N) loop computing |pos - cameraPos|² per
//     instance and writing a 4×4 matrix to either panelMesh or sphereMesh
//     per instance. At 4096 nodes: ~120 µs on M-class.
//   - Two materials' shaders compile once. No per-frame allocations except
//     the two scratch matrices reused below.
//
// API SURFACE: identical to the previous nodes.ts so VaultOrbit3D doesn't
// need to change. `mesh` is exported as a `Group` containing both instanced
// meshes; raycaster intersects either, dispatch resolves to the correct
// per-instance id.

import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import type { VaultDoc } from "@tether/storage";
import {
  setupForceLayout,
  stepForceLayout,
  disposeForceLayout,
  createDevice,
  FORCE_CONSTANTS,
  type ForceEdge,
  type ForceLayoutSetup,
} from "@tether/kernels";

export const TINT_HEX: Record<VaultDoc["tint"], string> = {
  cyan: "#67e8f9",
  violet: "#a78bfa",
  amber: "#fbbf24",
  rose: "#ff7a94",
};

export const NODE_BYTES = 16; // vec4 stride on the GPU side

// LOD constants (hysteresis band keeps frame-to-frame switches stable)
const LOD_NEAR = 8.0;   // closer than this → glass panel
const LOD_FAR = 8.5;    // farther than this → sphere fallback

// ── Ring seed positions (for the initial layout before sim settles) ─────────
const RINGS = [
  { radius: 3.0, tilt: 0.28, max: 8 },
  { radius: 4.5, tilt: 0.21, max: 16 },
  { radius: 6.2, tilt: 0.154, max: 24 },
  { radius: 8.4, tilt: 0.112, max: 4096 },
] as const;

export function ringSeedPos(i: number): { x: number; y: number; z: number } {
  let cursor = 0;
  for (let r = 0; r < RINGS.length; r++) {
    const ring = RINGS[r]!;
    if (i < cursor + ring.max) {
      const inRing = i - cursor;
      const a = (inRing * 0.61803) * Math.PI * 2;
      return {
        x: Math.cos(a) * ring.radius,
        y: -Math.sin(a) * ring.radius * Math.sin(ring.tilt),
        z: Math.sin(a) * ring.radius * Math.cos(ring.tilt),
      };
    }
    cursor += ring.max;
  }
  // Fallback: scatter on a sphere
  const u = (i * 0.7548776662) % 1;
  const v = (i * 0.5698402909) % 1;
  const theta = 2 * Math.PI * u;
  const phi = Math.acos(2 * v - 1);
  const r = 9.5;
  return {
    x: r * Math.sin(phi) * Math.cos(theta),
    y: r * Math.sin(phi) * Math.sin(theta),
    z: r * Math.cos(phi),
  };
}

export interface NodeFieldOpts {
  maxNodes?: number;
  /** A camera reference so per-frame LOD distance computation can read its
   *  world position. Optional — when missing, all instances render as
   *  panels (no LOD culling). */
  camera?: THREE.Camera;
}

export type LayoutMode = 'ring' | 'cluster';

/** Per-doc-type cluster anchor (deterministic — same type → same anchor). */
export interface NodeEnumEntry {
  id: string;
  pos: THREE.Vector3;
  tint: VaultDoc['tint'];
  title: string;
}

export interface NodeField {
  /** Group containing both panel + sphere instanced meshes. */
  mesh: THREE.Group;
  /** Raycaster pickable layer (proxy: tries both meshes). */
  positions: Float32Array;
  count(): number;
  idToIdx(id: string): number | undefined;
  idxToId(i: number): string | undefined;
  doc(id: string): VaultDoc | undefined;
  rebuild(docs: VaultDoc[], wikilinkEdges: Array<[string, string]>): Promise<void>;
  step(deltaSec: number, hoveredId: string | null, activeId: string | null): Promise<void>;
  applyHighlight(highlightSet: Set<string>, activeId: string | null): void;
  raycast(raycaster: THREE.Raycaster): string | null;
  /** Set the camera reference for LOD distance compute (optional). */
  setCamera(cam: THREE.Camera): void;
  /** Switch between ring (default) and cluster (group-by-front.type) layouts. */
  setLayoutMode(mode: LayoutMode): void;
  /** Read the world position for a single doc id (text-msdf consumer). */
  getNodeWorldPos(id: string): THREE.Vector3 | null;
  /** Enumerate every live node for the per-frame text-msdf overlay. The
   *  returned array is freshly allocated each call but the inner positions
   *  are CLONES of internal scratch — safe to retain. Order matches the
   *  internal idx → id mapping. */
  enumerateNodes(): NodeEnumEntry[];
  dispose(): void;
}

// ── Cluster anchors ──────────────────────────────────────────────────────────
// One anchor per VaultFrontmatter['type'] value, distributed around the brain
// core on a wide ring (radius 5.5) at varied tilts so types occupy distinct
// shells. Picked deterministically — type-string → fixed slot — so the same
// doc lands at the same anchor across rebuilds. Anchors live slightly off the
// y-plane to avoid all-coplanar visual flattening.
const CLUSTER_TYPES = [
  'project', 'concept', 'paper', 'experiment', 'meeting',
  'persona', 'daily', 'person', 'note', 'claude-session', 'agent',
] as const;
const CLUSTER_DEFAULT = '__other__';
const CLUSTER_RADIUS = 5.5;

function clusterAnchor(typeKey: string): { x: number; y: number; z: number } {
  const idx = (CLUSTER_TYPES as readonly string[]).indexOf(typeKey);
  const slot = idx >= 0 ? idx : CLUSTER_TYPES.length; // "other" sits at the end
  const total = CLUSTER_TYPES.length + 1;
  const a = (slot / total) * Math.PI * 2;
  // Vary y by slot parity so adjacent types don't sit on the same plane.
  const yTilt = ((slot % 3) - 1) * 1.4;
  return {
    x: Math.cos(a) * CLUSTER_RADIUS,
    y: yTilt,
    z: Math.sin(a) * CLUSTER_RADIUS,
  };
}

export async function createNodeField(opts: NodeFieldOpts = {}): Promise<NodeField> {
  const MAX_NODES = opts.maxNodes ?? 4096;
  let cameraRef: THREE.Camera | null = opts.camera ?? null;

  // ── Glass-panel mesh (close) ────────────────────────────────────────────
  // RoundedBoxGeometry: width=1.42, height=0.86, depth=0.06 (panel slab).
  // Match v1 ShowcasePanel proportions (vault-orbit.tsx:482).
  const panelGeom = new RoundedBoxGeometry(1.42, 0.86, 0.06, 3, 0.05);
  const panelMat = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    transmission: 0.95,
    thickness: 0.3,
    ior: 1.45,
    roughness: 0.05,
    metalness: 0.0,
    // attenuationColor is a per-MATERIAL uniform (no per-instance support
    // until WebGPU material variants land). We tint per-instance via the
    // standard instanceColor multiply pathway; attenuationColor stays neutral.
    attenuationColor: new THREE.Color(0xffffff),
    attenuationDistance: 0.4,
    clearcoat: 0.4,
    clearcoatRoughness: 0.18,
    transparent: true,
    depthWrite: false,
    toneMapped: false,
  });
  const panelMesh = new THREE.InstancedMesh(panelGeom, panelMat, MAX_NODES);
  panelMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  panelMesh.count = 0;
  panelMesh.frustumCulled = false;
  panelMesh.setColorAt(0, new THREE.Color(0xffffff));

  // ── Sphere fallback mesh (far) ──────────────────────────────────────────
  const sphereGeom = new THREE.SphereGeometry(0.18, 16, 16);
  const sphereMat = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    emissive: 0xffffff,
    emissiveIntensity: 0.85,
    roughness: 0.35,
    metalness: 0.15,
    transparent: true,
    opacity: 1.0,
    toneMapped: false,
  });
  const sphereMesh = new THREE.InstancedMesh(sphereGeom, sphereMat, MAX_NODES);
  sphereMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  sphereMesh.count = 0;
  sphereMesh.frustumCulled = false;
  sphereMesh.setColorAt(0, new THREE.Color(0xffffff));

  const meshGroup = new THREE.Group();
  meshGroup.add(panelMesh);
  meshGroup.add(sphereMesh);

  // ── Compute backend: WebGPU when available, CPU fallback otherwise ──────
  let gpu:
    | {
        device: GPUDevice;
        setup: ForceLayoutSetup | null;
        readbacks: GPUBuffer[];
        readbackBusy: boolean[];
        nextReadback: number;
        /** True while syncDocs is tearing down + recreating the kernel
         *  buffers. step() short-circuits during this window so we never
         *  submit work that references a destroyed buffer. */
        rebuilding: boolean;
        /** Pending step() promise. syncDocs awaits it before disposing
         *  buffers — otherwise an in-flight first-step diagnostic mapAsync
         *  inside stepForceLayout can land on a destroyed buffer. */
        stepInflight: Promise<unknown> | null;
      }
    | null = null;
  let cpuSim: CPUForceSim | null = null;

  let useGpu = false;
  if ("gpu" in navigator) {
    try {
      const dev = await createDevice();
      gpu = { device: dev.device, setup: null, readbacks: [], readbackBusy: [], nextReadback: 0, rebuilding: false, stepInflight: null };
      useGpu = true;
    } catch {
      useGpu = false;
    }
  }
  // Always construct a CPU sim too, so cluster mode can fall back to it
  // (GPU kernel doesn't yet support per-node anchors; cluster mode requires
  // CPU's anchor-aware step to actually visibly cluster).
  cpuSim = new CPUForceSim();
  if (!useGpu) {
    // eslint-disable-next-line no-console
    console.warn("[VaultOrbit3D] WebGPU unavailable — using CPU force sim fallback.");
  }

  // ── State ───────────────────────────────────────────────────────────────
  let nodeCount = 0;
  let positions = new Float32Array(0);
  let baseScale = new Float32Array(0); // scale derived from bytes
  /** Per-instance LOD state from the previous frame. true = panel, false = sphere. */
  let lodIsPanel = new Uint8Array(0);
  let idToIdxMap = new Map<string, number>();
  let idxToIdArr: string[] = [];
  let docByIdMap = new Map<string, VaultDoc>();
  let edges: Array<[string, string]> = [];
  let layoutMode: LayoutMode = 'ring';
  /** Per-instance cluster-mode anchor target (only populated in cluster mode).
   *  Stride 3: x,y,z. Length = nodeCount * 3. */
  let clusterAnchors = new Float32Array(0);

  // Reused scratch
  const tmpMat = new THREE.Matrix4();
  const tmpHidden = new THREE.Matrix4().makeScale(0, 0, 0);
  const tmpQuat = new THREE.Quaternion();
  const tmpScale = new THREE.Vector3(1, 1, 1);
  const tmpVec = new THREE.Vector3();
  const tmpColor = new THREE.Color();
  const camWorldPos = new THREE.Vector3();

  function logScale(bytes: number | undefined): number {
    const b = bytes ?? 1024;
    return 0.7 + Math.min(1.6, Math.log10(b / 100) * 0.4);
  }

  async function rebuild(docs: VaultDoc[], wikilinkEdges: Array<[string, string]>) {
    nodeCount = Math.min(docs.length, MAX_NODES);
    panelMesh.count = nodeCount;
    sphereMesh.count = nodeCount;
    positions = new Float32Array(nodeCount * 4);
    baseScale = new Float32Array(nodeCount);
    lodIsPanel = new Uint8Array(nodeCount);
    idToIdxMap = new Map();
    idxToIdArr = new Array(nodeCount);
    docByIdMap = new Map();
    clusterAnchors = new Float32Array(nodeCount * 3);

    for (let i = 0; i < nodeCount; i++) {
      const d = docs[i];
      if (!d) continue;
      idToIdxMap.set(d.id, i);
      idxToIdArr[i] = d.id;
      docByIdMap.set(d.id, d);
      const pos = ringSeedPos(i);
      positions[i * 4 + 0] = pos.x + (Math.random() - 0.5) * 0.2;
      positions[i * 4 + 1] = pos.y + (Math.random() - 0.5) * 0.2;
      positions[i * 4 + 2] = pos.z + (Math.random() - 0.5) * 0.2;
      positions[i * 4 + 3] = 0;
      baseScale[i] = logScale(d.bytes);
      lodIsPanel[i] = 1; // default: assume close until first frame proves otherwise
      const hex = TINT_HEX[d.tint] ?? TINT_HEX.violet;
      tmpColor.setStyle(hex);
      panelMesh.setColorAt(i, tmpColor);
      sphereMesh.setColorAt(i, tmpColor);
      // Cluster anchor — derived from front.type with deterministic jitter
      // (golden-ratio walk so same-type docs don't pile on top of each other).
      const typeKey = (d.front?.type as string | undefined) ?? CLUSTER_DEFAULT;
      const a = clusterAnchor(typeKey);
      const jitterAngle = (i * 0.61803) * Math.PI * 2;
      const jitterRadius = 1.0 + (i * 0.37) % 0.6;
      clusterAnchors[i * 3 + 0] = a.x + Math.cos(jitterAngle) * jitterRadius;
      clusterAnchors[i * 3 + 1] = a.y + ((i * 0.13) % 1.0 - 0.5) * 1.2;
      clusterAnchors[i * 3 + 2] = a.z + Math.sin(jitterAngle) * jitterRadius;
    }
    if (panelMesh.instanceColor) panelMesh.instanceColor.needsUpdate = true;
    if (sphereMesh.instanceColor) sphereMesh.instanceColor.needsUpdate = true;
    edges = wikilinkEdges;

    const gpuEdges: ForceEdge[] = [];
    for (const [a, b] of edges) {
      const ia = idToIdxMap.get(a);
      const ib = idToIdxMap.get(b);
      if (ia === undefined || ib === undefined) continue;
      gpuEdges.push({ a: ia, b: ib, kind: 0 });
    }

    const initial = new Float32Array(nodeCount * 3);
    for (let i = 0; i < nodeCount; i++) {
      initial[i * 3 + 0] = positions[i * 4 + 0]!;
      initial[i * 3 + 1] = positions[i * 4 + 1]!;
      initial[i * 3 + 2] = positions[i * 4 + 2]!;
    }

    if (useGpu && gpu) {
      // Lock out step() while we tear down + rebuild. Without this, an
      // in-flight stepForceLayout / copyBufferToBuffer can reference a
      // buffer we're about to destroy — surfaces as
      //   "[Buffer ...] used in submit while destroyed"
      //   "copyBufferToBuffer parameter 3 is not of type 'GPUBuffer'"
      gpu.rebuilding = true;
      // Wait for any in-flight step() (which may be mid-mapAsync on the
      // diag readback buffer) to finish before we tear down its buffers.
      if (gpu.stepInflight) {
        try {
          await gpu.stepInflight;
        } catch {
          /* ok */
        }
      }
      // Flush any work the previous frame submitted before destroying the
      // buffers it referenced.
      try {
        await gpu.device.queue.onSubmittedWorkDone();
      } catch {
        /* ok */
      }
      if (gpu.setup) {
        try {
          disposeForceLayout(gpu.setup);
        } catch {
          /* ok */
        }
        gpu.setup = null;
      }
      for (const rb of gpu.readbacks) {
        try {
          rb.destroy();
        } catch {
          /* ok */
        }
      }
      gpu.readbacks = [];
      gpu.readbackBusy = [];
      gpu.nextReadback = 0;

      if (nodeCount > 0) {
        gpu.setup = await setupForceLayout(gpu.device, nodeCount, gpuEdges, initial);
        for (let i = 0; i < 2; i++) {
          gpu.readbacks.push(
            gpu.device.createBuffer({
              label: `vault-orbit-readback-${i}`,
              size: nodeCount * NODE_BYTES,
              usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
            }),
          );
          gpu.readbackBusy.push(false);
        }
      }
      gpu.rebuilding = false;
    }
    // Always rebuild the CPU sim too — cluster mode runs on CPU regardless
    // of GPU availability, so it needs the current node graph.
    if (cpuSim) {
      cpuSim.rebuild(nodeCount, idxToIdArr, idToIdxMap);
    }

    applyMatrices(null, null);
  }

  /**
   * Walk every instance, decide its LOD bucket using the hysteresis band
   * around the camera's world distance, then write either the visible
   * matrix into its mesh + the hidden (scale=0) matrix into the other, or
   * vice versa.
   */
  /** Lazy camera lookup: walk the scene graph the first time a frame fires
   *  with the meshGroup attached, find any PerspectiveCamera, cache it. */
  function resolveCamera(): THREE.Camera | null {
    if (cameraRef) return cameraRef;
    const root = meshGroup.parent; // expected to be the THREE.Scene
    if (!root) return null;
    let found: THREE.Camera | null = null;
    root.traverse((obj) => {
      if (found) return;
      if ((obj as THREE.PerspectiveCamera).isCamera) {
        found = obj as THREE.Camera;
      }
    });
    if (found) cameraRef = found;
    return cameraRef;
  }

  function applyMatrices(hoveredId: string | null, activeId: string | null) {
    const cam = resolveCamera();
    if (cam) cam.getWorldPosition(camWorldPos);
    const cx = cam ? camWorldPos.x : 0;
    const cy = cam ? camWorldPos.y : 0;
    const cz = cam ? camWorldPos.z : 0;
    const haveCam = !!cam;

    for (let i = 0; i < nodeCount; i++) {
      const x = positions[i * 4 + 0]!;
      const y = positions[i * 4 + 1]!;
      const z = positions[i * 4 + 2]!;
      let s = baseScale[i] ?? 1.0;
      const id = idxToIdArr[i];
      if (id) {
        if (id === activeId) s *= 1.6;
        else if (id === hoveredId) s *= 1.3;
      }
      tmpVec.set(x, y, z);
      tmpScale.set(s, s, s);
      tmpMat.compose(tmpVec, tmpQuat, tmpScale);

      // LOD with hysteresis. When no camera is set, default to panel mode
      // (closer is the more flattering look).
      let isPanel: boolean;
      if (haveCam) {
        const dx = x - cx;
        const dy = y - cy;
        const dz = z - cz;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const prev = lodIsPanel[i] === 1;
        if (prev) {
          // Was panel; switch to sphere only past the far threshold
          isPanel = dist <= LOD_FAR;
        } else {
          // Was sphere; switch to panel only inside the near threshold
          isPanel = dist <= LOD_NEAR;
        }
        lodIsPanel[i] = isPanel ? 1 : 0;
      } else {
        isPanel = true;
        lodIsPanel[i] = 1;
      }

      if (isPanel) {
        panelMesh.setMatrixAt(i, tmpMat);
        sphereMesh.setMatrixAt(i, tmpHidden);
      } else {
        sphereMesh.setMatrixAt(i, tmpMat);
        panelMesh.setMatrixAt(i, tmpHidden);
      }
    }
    panelMesh.instanceMatrix.needsUpdate = true;
    sphereMesh.instanceMatrix.needsUpdate = true;
  }

  async function step(
    deltaSec: number,
    hoveredId: string | null,
    activeId: string | null,
  ): Promise<void> {
    if (nodeCount === 0) return;
    const dt = Math.min(deltaSec, FORCE_CONSTANTS.CAP_DT_MAX);

    // Cluster mode runs on the CPU sim so anchors work — the GPU kernel
    // doesn't bake per-node anchors yet, and the CPU readback gets clobbered
    // by the GPU's still-ring posOutBuf every readback.
    const useGpuThisStep =
      useGpu && gpu && gpu.setup && !gpu.rebuilding && layoutMode !== 'cluster';

    if (useGpuThisStep && gpu && gpu.setup) {
      const localGpu = gpu;
      const localSetup = gpu.setup;
      const stepPromise = (async () => {
        await stepForceLayout(localSetup, dt);
      })();
      localGpu.stepInflight = stepPromise;
      try {
        await stepPromise;
        // syncDocs may have started during the await — bail out before
        // submitting a readback against the (now-destroyed) buffers.
        if (gpu.rebuilding || !gpu.setup) return;
        const slot = gpu.nextReadback;
        if (!gpu.readbackBusy[slot]) {
          gpu.readbackBusy[slot] = true;
          gpu.nextReadback = (gpu.nextReadback + 1) % gpu.readbacks.length;
          const enc = gpu.device.createCommandEncoder({ label: `vault-orbit-rb-${slot}` });
          enc.copyBufferToBuffer(
            gpu.setup.posOutBuf,
            0,
            gpu.readbacks[slot]!,
            0,
            nodeCount * NODE_BYTES,
          );
          gpu.device.queue.submit([enc.finish()]);
          gpu.readbacks[slot]!.mapAsync(GPUMapMode.READ)
            .then(() => {
              const view = new Float32Array(gpu!.readbacks[slot]!.getMappedRange().slice(0));
              gpu!.readbacks[slot]!.unmap();
              if (view.length === positions.length) positions.set(view);
              gpu!.readbackBusy[slot] = false;
            })
            .catch(() => {
              gpu!.readbackBusy[slot] = false;
            });
        }
      } catch (err) {
        // Buffers destroyed mid-flight by a concurrent syncDocs is the
        // expected race when docs change rapidly — silence those; surface
        // genuine kernel/GPU errors only.
        const msg = err instanceof Error ? err.message : String(err);
        const isLifecycleRace =
          /destroyed|invalid GPU|param.*GPUBuffer/i.test(msg) && (gpu?.rebuilding ?? false);
        if (!isLifecycleRace) {
          // eslint-disable-next-line no-console
          console.error("[VaultOrbit3D] stepForceLayout failed:", err);
        }
      } finally {
        if (localGpu.stepInflight === stepPromise) localGpu.stepInflight = null;
      }
    } else if (cpuSim) {
      cpuSim.step(dt, edges, positions, layoutMode === 'cluster' ? clusterAnchors : null);
    }

    // (Cluster nudge removed — cluster mode now runs on CPU sim above, which
    // bakes anchor attraction into the force step natively.)

    applyMatrices(hoveredId, activeId);
  }

  function applyHighlight(highlightSet: Set<string>, activeId: string | null) {
    const dim = highlightSet.size > 0;
    for (let i = 0; i < nodeCount; i++) {
      const id = idxToIdArr[i];
      const d = id ? docByIdMap.get(id) : null;
      const baseHex = d ? (TINT_HEX[d.tint] ?? TINT_HEX.violet) : TINT_HEX.violet;
      tmpColor.setStyle(baseHex);
      if (id && id === activeId) {
        tmpColor.multiplyScalar(1.7);
      } else if (dim && id && !highlightSet.has(id)) {
        tmpColor.multiplyScalar(0.22);
      } else if (dim && id && highlightSet.has(id)) {
        tmpColor.multiplyScalar(1.5);
      }
      panelMesh.setColorAt(i, tmpColor);
      sphereMesh.setColorAt(i, tmpColor);
    }
    if (panelMesh.instanceColor) panelMesh.instanceColor.needsUpdate = true;
    if (sphereMesh.instanceColor) sphereMesh.instanceColor.needsUpdate = true;
  }

  function raycast(raycaster: THREE.Raycaster): string | null {
    if (nodeCount === 0) return null;
    // Hit-test both LOD layers; pick the closest hit.
    const hitsPanel = raycaster.intersectObject(panelMesh, false);
    const hitsSphere = raycaster.intersectObject(sphereMesh, false);
    let best: { dist: number; idx: number } | null = null;
    for (const h of hitsPanel) {
      if (h.instanceId === undefined || h.instanceId < 0) continue;
      // Panels at scale-0 (hidden) still register in raycast geometry; skip
      // those by checking the LOD bucket.
      if (lodIsPanel[h.instanceId] !== 1) continue;
      if (!best || h.distance < best.dist) best = { dist: h.distance, idx: h.instanceId };
    }
    for (const h of hitsSphere) {
      if (h.instanceId === undefined || h.instanceId < 0) continue;
      if (lodIsPanel[h.instanceId] !== 0) continue;
      if (!best || h.distance < best.dist) best = { dist: h.distance, idx: h.instanceId };
    }
    if (!best) return null;
    return idxToIdArr[best.idx] ?? null;
  }

  return {
    mesh: meshGroup,
    get positions() {
      return positions;
    },
    count: () => nodeCount,
    idToIdx: (id: string) => idToIdxMap.get(id),
    idxToId: (i: number) => idxToIdArr[i],
    doc: (id: string) => docByIdMap.get(id),
    rebuild,
    step,
    applyHighlight,
    raycast,
    setCamera(cam: THREE.Camera) {
      cameraRef = cam;
    },
    setLayoutMode(mode: LayoutMode) {
      layoutMode = mode;
    },
    getNodeWorldPos(id: string): THREE.Vector3 | null {
      const idx = idToIdxMap.get(id);
      if (idx === undefined) return null;
      return new THREE.Vector3(
        positions[idx * 4 + 0] ?? 0,
        positions[idx * 4 + 1] ?? 0,
        positions[idx * 4 + 2] ?? 0,
      );
    },
    enumerateNodes(): NodeEnumEntry[] {
      const out: NodeEnumEntry[] = [];
      for (let i = 0; i < nodeCount; i++) {
        const id = idxToIdArr[i];
        if (!id) continue;
        const d = docByIdMap.get(id);
        if (!d) continue;
        out.push({
          id,
          pos: new THREE.Vector3(
            positions[i * 4 + 0] ?? 0,
            positions[i * 4 + 1] ?? 0,
            positions[i * 4 + 2] ?? 0,
          ),
          tint: d.tint,
          title: d.title,
        });
      }
      return out;
    },
    dispose() {
      panelGeom.dispose();
      panelMat.dispose();
      sphereGeom.dispose();
      sphereMat.dispose();
      try {
        if (gpu?.setup) disposeForceLayout(gpu.setup);
      } catch {
        /* ok */
      }
      for (const rb of gpu?.readbacks ?? []) {
        try {
          rb.destroy();
        } catch {
          /* ok */
        }
      }
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────
// CPU force sim — only runs when WebGPU isn't available. Constants pulled
// from FORCE_CONSTANTS (which themselves came from v1 vault-orbit.tsx:164-173).
// ────────────────────────────────────────────────────────────────────────────

class CPUForceSim {
  private idToIdx: Map<string, number> = new Map();
  private idxToId: string[] = [];
  private velocities: Float32Array = new Float32Array(0);
  private n = 0;

  rebuild(n: number, idxToId: string[], idToIdx: Map<string, number>) {
    this.n = n;
    this.idxToId = idxToId;
    this.idToIdx = idToIdx;
    this.velocities = new Float32Array(n * 4);
  }

  step(
    dt: number,
    edges: Array<[string, string]>,
    positions: Float32Array,
    /** Optional cluster anchors (stride-3). When provided, the centring force
     *  is replaced with a per-node attraction toward `anchors[i*3..]`. */
    anchors: Float32Array | null = null,
  ) {
    const N = this.n;
    if (N === 0) return;
    const { K_REPEL, MIN_D2, K_SPRING, REST, CENTER_K, DAMPING, MAX_V, CAP_DT_MAX } =
      FORCE_CONSTANTS;
    const cap_dt = Math.min(dt, CAP_DT_MAX);
    // Cluster attraction is roughly 2× the centring constant — tuned so docs
    // settle toward their type-anchor without overpowering wikilink springs.
    const ANCHOR_K = CENTER_K * 2.2;

    const fx = new Float32Array(N);
    const fy = new Float32Array(N);
    const fz = new Float32Array(N);

    for (let i = 0; i < N; i++) {
      const xi = positions[i * 4 + 0]!;
      const yi = positions[i * 4 + 1]!;
      const zi = positions[i * 4 + 2]!;
      for (let j = i + 1; j < N; j++) {
        const dx = xi - positions[j * 4 + 0]!;
        const dy = yi - positions[j * 4 + 1]!;
        const dz = zi - positions[j * 4 + 2]!;
        const d2 = Math.max(MIN_D2, dx * dx + dy * dy + dz * dz);
        const inv = 1 / Math.sqrt(d2);
        const k = K_REPEL / d2;
        const ux = dx * inv * k;
        const uy = dy * inv * k;
        const uz = dz * inv * k;
        fx[i] = (fx[i] ?? 0) + ux;
        fy[i] = (fy[i] ?? 0) + uy;
        fz[i] = (fz[i] ?? 0) + uz;
        fx[j] = (fx[j] ?? 0) - ux;
        fy[j] = (fy[j] ?? 0) - uy;
        fz[j] = (fz[j] ?? 0) - uz;
      }
    }

    for (const [aId, bId] of edges) {
      const ia = this.idToIdx.get(aId);
      const ib = this.idToIdx.get(bId);
      if (ia === undefined || ib === undefined) continue;
      const dx = positions[ib * 4 + 0]! - positions[ia * 4 + 0]!;
      const dy = positions[ib * 4 + 1]! - positions[ia * 4 + 1]!;
      const dz = positions[ib * 4 + 2]! - positions[ia * 4 + 2]!;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 0.01;
      const k = (K_SPRING * (dist - REST)) / dist;
      fx[ia] = (fx[ia] ?? 0) + dx * k;
      fy[ia] = (fy[ia] ?? 0) + dy * k;
      fz[ia] = (fz[ia] ?? 0) + dz * k;
      fx[ib] = (fx[ib] ?? 0) - dx * k;
      fy[ib] = (fy[ib] ?? 0) - dy * k;
      fz[ib] = (fz[ib] ?? 0) - dz * k;
    }

    for (let i = 0; i < N; i++) {
      if (anchors) {
        // Pull toward the per-node cluster anchor instead of the world origin.
        const ax = anchors[i * 3 + 0]!;
        const ay = anchors[i * 3 + 1]!;
        const az = anchors[i * 3 + 2]!;
        fx[i] = (fx[i] ?? 0) + (ax - positions[i * 4 + 0]!) * ANCHOR_K;
        fy[i] = (fy[i] ?? 0) + (ay - positions[i * 4 + 1]!) * ANCHOR_K;
        fz[i] = (fz[i] ?? 0) + (az - positions[i * 4 + 2]!) * ANCHOR_K;
      } else {
        fx[i] = (fx[i] ?? 0) - positions[i * 4 + 0]! * CENTER_K;
        fy[i] = (fy[i] ?? 0) - positions[i * 4 + 1]! * CENTER_K;
        fz[i] = (fz[i] ?? 0) - positions[i * 4 + 2]! * CENTER_K;
      }

      let vx = (this.velocities[i * 4 + 0]! + (fx[i] ?? 0) * cap_dt) * DAMPING;
      let vy = (this.velocities[i * 4 + 1]! + (fy[i] ?? 0) * cap_dt) * DAMPING;
      let vz = (this.velocities[i * 4 + 2]! + (fz[i] ?? 0) * cap_dt) * DAMPING;

      const speed2 = vx * vx + vy * vy + vz * vz;
      if (speed2 > MAX_V * MAX_V) {
        const s = MAX_V / Math.sqrt(speed2);
        vx *= s;
        vy *= s;
        vz *= s;
      }
      this.velocities[i * 4 + 0] = vx;
      this.velocities[i * 4 + 1] = vy;
      this.velocities[i * 4 + 2] = vz;
      positions[i * 4 + 0] = positions[i * 4 + 0]! + vx * cap_dt;
      positions[i * 4 + 1] = positions[i * 4 + 1]! + vy * cap_dt;
      positions[i * 4 + 2] = positions[i * 4 + 2]! + vz * cap_dt;
    }
  }
}
